/* eslint-disable react-refresh/only-export-components */
import AchievementCounter from '@/components/Achievements/AchievementCounter';
import { measure } from '@/components/Analytics/Analytics';
import CollectionCard, {
	CollectionCardTarget,
} from '@/components/Collections/CollectionCard/CollectionCard';
import ArrowLeftIcon from '@/components/Icon/ArrowLeftIcon';
import SearchIcon from '@/components/Icon/SearchIcon';
import IconButton from '@/components/IconButton/IconButton';
import useCalendar from '@/hooks/calendar/useCalendar';
import useCollections from '@/hooks/collections/useCollections';
import useSpecies from '@/hooks/species/useSpecies';
import useHaptics from '@/hooks/useHaptics';
import useSeason, {
	getDutchSeasonName,
	getSeasonName,
} from '@/hooks/useSeason';
import useUrlState from '@/hooks/useUrlState';
import { SpeciesAttributes, StrapiEntry } from '@/lib/apiTypes';
import cn from '@/lib/cs';
import { motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';

export const openSearchIfBarIsVisible = () => {
	const searchInput = document.querySelector(
		'[role="search"] input[type="search"]',
	) as HTMLInputElement;
	searchInput?.focus();
};

export const useSearchVisibilityIfBarIsInvisible = () => {
	const { state: query, setState: setQuery } = useUrlState('q', true);

	const openSearch = useCallback<() => void>(() => {
		setQuery('');
		measure('search-open');
	}, [setQuery]);

	useEffect(() => {
		if (query === null || query !== '') return;
		openSearchIfBarIsVisible();
	}, [query]);

	return {
		openSearch,
	};
};

export type SearchProps = {
	alwaysShowSearchBar?: boolean;
	searchResultTarget?: CollectionCardTarget;
};

const Search = ({
	alwaysShowSearchBar,
	searchResultTarget = 'details',
}: SearchProps) => {
	const reduceMotion = useReducedMotion();
	const { species } = useSpecies();

	const { state: query, deleteState: deleteQuery } = useUrlState(
		'q',
		!alwaysShowSearchBar,
	);

	const showResults = useMemo(() => typeof query === 'string', [query]);

	const showResultsCombined = showResults;

	const onEscapePressed = useCallback(
		(event: KeyboardEvent) => {
			if (
				showResults &&
				(event.key === 'Escape' || event.key === 'Esc')
			) {
				measure('search-close', 'exit');
				deleteQuery();
			}
		},
		[deleteQuery, showResults],
	);

	useEffect(() => {
		window.addEventListener('keydown', onEscapePressed);
		return () => {
			window.removeEventListener('keydown', onEscapePressed);
		};
	}, [onEscapePressed]);

	if (!species) return null;

	return (
		<>
			{showResultsCombined && (
				<motion.div
					className="fixed inset-0 z-40 size-full bg-black"
					initial={{
						y: reduceMotion ? 0 : '-100%',
						opacity: 0,
					}}
					animate={{
						y: 0,
						opacity: 1,
						transition: {
							duration: 0.3,
							ease: 'easeOut',
						},
					}}>
					<div
						className="absolute inset-0 z-40 size-full bg-accent opacity-50"
						onClick={(event) => event.stopPropagation()}
					/>
				</motion.div>
			)}
			<SearchContent
				alwaysShowSearchBar={alwaysShowSearchBar}
				showResults={showResultsCombined}
				searchResultTarget={searchResultTarget}
			/>
		</>
	);
};

const SearchContent = ({
	alwaysShowSearchBar,
	showResults,
	searchResultTarget,
}: {
	alwaysShowSearchBar?: boolean;
	showResults: boolean;
	searchResultTarget: CollectionCardTarget;
}) => {
	const reduceMotion = useReducedMotion();
	const { withOnButtonPress } = useHaptics();
	const backButtonRef = useRef<HTMLButtonElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const hintId = useId();

	// Get query
	const {
		state: query,
		setState: setQuery,
		deleteState: deleteQuery,
	} = useUrlState('q', !alwaysShowSearchBar);

	// Functions to use for search
	const { species } = useSpecies();
	const { getDatesFromSpecies } = useCalendar();
	const { findCollectionsOfSpeciesId } = useCollections();
	const season = useSeason();

	// Blur input if query is not in url (null)
	useEffect(() => {
		if (
			query === null &&
			inputRef.current &&
			document.activeElement === inputRef.current
		) {
			inputRef.current.blur();
		}
	}, [query]);

	// Get results
	const results = useMemo<StrapiEntry<SpeciesAttributes>[]>(() => {
		if (species?.data === undefined) return [];
		if (query === null || query.length < 3) return [];

		const queryLowerCase = query.toLowerCase();

		// Search for matching names
		const matchingNames = species.data.filter((singleSpecies) => {
			return `${singleSpecies.attributes.titleLong} ${singleSpecies.attributes.titleShort}`
				.toLowerCase()
				.includes(queryLowerCase);
		});

		// Search for matching month or season names
		const matchingMonths = species.data.filter((singleSpecies) => {
			const dates = getDatesFromSpecies(singleSpecies.id);
			if (!dates) return false;
			return dates
				.map(
					(date) =>
						`${date
							.toLocaleString('nl-NL', { month: 'long' })
							.toLowerCase()} ${getDutchSeasonName(
							getSeasonName(date),
						).toLowerCase()}`,
				)
				.join(' ')
				.includes(queryLowerCase);
		});

		// Search for matching collection names
		const matchingCollectionNames = species.data.filter((singleSpecies) => {
			return findCollectionsOfSpeciesId(singleSpecies.id)
				.map(
					(collection) =>
						`${collection.attributes.titleLong} ${collection.attributes.titleShort}`,
				)
				.join(' ')
				.toLowerCase()
				.includes(queryLowerCase);
		});

		// Search for matching descriptions
		const matchingDescriptions = species.data.filter((singleSpecies) => {
			return Object.values(singleSpecies.attributes.speciesDescription)
				.map((description) => description?.description ?? '')
				.join(' ')
				.toLowerCase()
				.includes(queryLowerCase);
		});

		const results =
			// Combine results
			[
				...matchingNames,
				...matchingMonths,
				...matchingCollectionNames,
				...matchingDescriptions,
			]
				// Remove duplicates
				.filter((value, index, self) => self.indexOf(value) === index)
				// Show only the first 15 results
				.slice(0, 15);

		return results;
	}, [species, query, getDatesFromSpecies, findCollectionsOfSpeciesId]);

	const closeSearch = () => {
		deleteQuery();
		measure('search-close', 'exit');
	};

	const showSearchBar = alwaysShowSearchBar || showResults;

	return (
		<section
			className={cn(
				' inset-x-0 top-0 w-full z-50',
				showResults ? 'fixed h-full' : 'sticky',
			)}
			onClick={closeSearch}>
			<div
				className={cn(
					'relative w-full max-h-screen overflow-y-auto',
					showResults ? 'h-full' : 'h-18',
				)}>
				{showResults && (
					<button
						className="sr-only"
						onFocus={() => {
							if (query?.length) backButtonRef.current?.focus();
							else deleteQuery();
						}}
					/>
				)}
				{showSearchBar && (
					<div className="sticky top-0 z-50">
						<div
							className="relative mx-auto grid w-full max-w-[40rem] gap-4 p-4"
							onClick={(event) => event.stopPropagation()}>
							{showResults && (
								<IconButton
									ref={backButtonRef}
									aria-label="Terug"
									title="Terug"
									icon={
										<ArrowLeftIcon className="size-8 lg:size-8" />
									}
									className="absolute start-2 top-2 z-50"
									onClick={closeSearch}
									initial={{
										scale: 1,
									}}
								/>
							)}
							<div
								className={`relative flex grow items-stretch justify-stretch gap-2 rounded-full text-white ${
									showResults ? 'ps-16' : ''
								}`}>
								<div
									role="search"
									aria-label="Zoeken"
									className="relative grow rounded-full bg-overlay">
									<SearchIcon className="absolute start-0 size-10 ps-4 pt-1.5 text-primary lg:size-10" />
									<input
										ref={inputRef}
										type="search"
										placeholder="Zoek"
										aria-label="Zoek naar een soort"
										aria-describedby={
											showResults ? hintId : undefined
										}
										className="w-full rounded-full bg-transparent p-3 ps-14 text-base text-primary placeholder:text-primary"
										value={query || ''}
										onChange={(e) =>
											setQuery(e.target.value)
										}
										onFocus={() => {
											if (!query) {
												setQuery('');
												measure('search-open');
											}
										}}
										{...withOnButtonPress}
									/>
								</div>
								{!showResults && <AchievementCounter />}
							</div>
						</div>
					</div>
				)}
				{showResults && (
					<>
						<motion.img
							src={`/assets/illustrations/top_end_${season}.svg`}
							className="absolute end-0 top-0 max-w-xs opacity-30"
							alt=""
							initial={{
								y: reduceMotion ? '-10%' : '-25%',
							}}
							animate={{
								y: '-10%',
							}}
						/>
						<p
							role="status"
							aria-live="polite"
							className="sr-only -z-10">
							{results.length} resultaten
						</p>
						{results.length === 0 && (
							<p
								id={hintId}
								className="relative p-4 text-center text-white">
								Zoek op een naam, maand of seizoen. Probeer
								eens: slak, januari of herfst!
							</p>
						)}
						<ol
							aria-label="Resultaten"
							className="relative z-40 mx-auto grid min-h-fit w-full max-w-xl grid-cols-3 items-center justify-center gap-4 p-2 pb-[40vh] lg:pb-32"
							onClick={(event) => event.stopPropagation()}>
							{results.map((result, index) => (
								<CollectionCard
									key={result.id}
									layoutId={`search-result-${result.id}`}
									index={index}
									speciesId={result.id}
									target={searchResultTarget}
									onClick={() => {
										measure(
											`search-result-${result.id}-${result.attributes.slug}-${searchResultTarget}`,
											'navigation',
										);
										closeSearch();
									}}
								/>
							))}
						</ol>
					</>
				)}
				{showResults && (
					<button
						className="sr-only"
						onFocus={() => {
							if (query?.length) backButtonRef.current?.focus();
							else deleteQuery();
						}}
					/>
				)}
			</div>
		</section>
	);
};

export default Search;
